import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Collapse,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Code as CodeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { QueryResult } from '../types/api';

interface ResultsSectionProps {
  results: QueryResult | null;
}

const ResultsSection: React.FC<ResultsSectionProps> = ({ results }) => {
  const [showSql, setShowSql] = useState(true);
  const [showDebug, setShowDebug] = useState(false);

  if (!results) {
    return null;
  }

  // Extract column names from the first result row if available
  const columns = results.data.length > 0 ? Object.keys(results.data[0]) : [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Query Results
        {results.data.length > 0 && (
          <Chip
            label={`${results.data.length} rows`}
            size="small"
            color="primary"
            sx={{ ml: 2 }}
          />
        )}
      </Typography>

      {results.sql_query && (
        <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 1,
              backgroundColor: 'action.hover',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CodeIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="subtitle2">SQL Query</Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowSql(!showSql)}>
              {showSql ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showSql}>
            <Box
              component="pre"
              sx={{
                p: 2,
                m: 0,
                overflowX: 'auto',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                borderRadius: 0,
              }}
            >
              {results.sql_query}
            </Box>
          </Collapse>
        </Paper>
      )}

      {results.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {results.error}
        </Alert>
      ) : results.data.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Query executed successfully, but no results were returned.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                {columns.map((column) => (
                  <TableCell key={column}>
                    <Typography variant="subtitle2">{column}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.data.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  {columns.map((column) => (
                    <TableCell key={`${rowIndex}-${column}`}>
                      {row[column] !== null && row[column] !== undefined
                        ? String(row[column])
                        : 'NULL'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {results.debug_info && (
        <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 1,
              backgroundColor: 'action.hover',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="subtitle2">Debug Information</Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowDebug(!showDebug)}>
              {showDebug ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showDebug}>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                {results.debug_info}
              </Typography>
            </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default ResultsSection;
